// What the AE's side of handoffs says (spec C6, C7.3, C7.4; S15 batch 4; the Renewals and Record
// boards): the Reason pill, the Act by cell, From and Sent, the Finished tab's Result, Accept and
// Send back with their confirms and toasts, the AE's banner on the Record and the note the VA reads
// after a send back. Pure, so it is tested without a browser.
import type {
  BackFromAe,
  FinishedHandoff,
  HandoffClose,
  LostReason,
  OpenHandoff,
  RenewalRow,
  RenewalStatus,
  StepsView,
} from '../auth/api';
import { parseDate, parseInput } from './fix-lines';
import { reasonLabels as reassignReasonLabels } from './reassign-lines';
import { handoffReasonLabels, sentWhen } from './handoff-lines';
import { listDate, money, nyToday, statuses, when, withMeReason } from './renewal-lines';

type Handoff = Pick<OpenHandoff, 'kind' | 'reason' | 'reassignReason' | 'otherText'>;

/** The kit's status pill for a reason that sets one; the others are grey (the Renewals board). */
const reasonPills: Partial<Record<string, string>> = {
  quote: 's-quote',
  increase: 's-increase',
  non_renewal: 's-nrwl',
  noc: 's-noc',
};

/** The Reason pill's words: the handoff reason, or the reassign request's reason. */
/**
 * A take over has no reason (decision 1, Nov 1): the row keeps Other with these words for the
 * database only (takeOverText in apps/server/src/handoffs.ts). Passed on, it reads "Take over".
 */
const takeOverText = 'Take over';
const isTakeOverReason = (h: Handoff) => h.reason === 'other' && h.otherText === takeOverText;

export function reasonLabel(h: Handoff): string {
  if (h.kind === 'take_over') return '';
  if (isTakeOverReason(h)) return takeOverText;
  if (h.kind === 'reassign_to_ae') {
    const why = h.reassignReason ? reassignReasonLabels[h.reassignReason] : null;
    return why ? `Reassign: ${why}` : 'Reassign';
  }
  return h.reason ? handoffReasonLabels[h.reason] : '';
}

/** The Reason pill's class: the matching status color, else grey. */
export const reasonPillCls = (h: Pick<Handoff, 'reason'>) =>
  `pill ${reasonPills[h.reason ?? ''] ?? 't-grey'}`;

/** The Reason with Other's own words, for the Quick view and the Record banner. */
export const reasonLong = (h: Handoff) =>
  h.kind !== 'take_over' && !isTakeOverReason(h) && h.reason === 'other' && h.otherText
    ? `Other: ${h.otherText}`
    : reasonLabel(h);

/**
 * The handoff a row on the AE's tabs is about: on Finished the one that ended (a newer one may be
 * open, a pass or a new Hand to AE; Codex on PR 286), elsewhere the open one.
 */
export const shownHandoff = (
  r: Pick<RenewalRow, 'handoff' | 'finishedHandoff'>,
  finished = false,
): (Handoff & Pick<OpenHandoff, 'createdBy' | 'createdAt'>) | null =>
  (finished ? (r.finishedHandoff ?? r.handoff) : (r.handoff ?? r.finishedHandoff)) ?? null;

/**
 * The Reason cell of a row on the AE's tabs: the shown handoff's, else Increase for a reshop (no
 * handoff row yet; Batch 9 makes it one). Null when there is none.
 */
export function rowReason(r: RenewalRow, finished = false): { label: string; cls: string } | null {
  const h = shownHandoff(r, finished);
  if (h) {
    const label = reasonLabel(h);
    return label ? { label, cls: reasonPillCls(h) } : null;
  }
  const reshop = withMeReason(r);
  return reshop ? { label: reshop, cls: 'pill s-increase' } : null;
}

const dayCount = (from: string, to: string) =>
  Math.round(
    (new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000,
  );

/** The Act by rule in words, for its tooltip (spec C6; the values seeded in Settings). */
export function actByRule(h: Handoff): string {
  if (h.kind === 'reassign_to_ae') return 'Reassign request: 2 days after it was sent.';
  switch (h.reason) {
    case 'quote':
    case 'increase':
      return `${reasonLabel(h)}: 30 days before expiry, or 2 days after it was sent if later.`;
    case 'non_renewal':
      return 'Non-renewal: 7 days before expiry, or 2 days after it was sent if later.';
    default:
      return `${reasonLabel(h) || 'This reason'}: 2 days after it was sent.`;
  }
}

/**
 * The Act by chip (kit `actby`): amber within 3 days, red and "Overdue" once past while it still
 * waits (spec C6). Null for a take over, which has no Act by date.
 */
export function actByChip(
  h: Pick<OpenHandoff, 'actBy' | 'state'> & Handoff,
  now: Date,
  /** The Record banner's words: "Act by Nov 14", "Overdue, act by Nov 11". */
  long = false,
): { text: string; cls: string; tip: string } | null {
  if (!h.actBy) return null;
  const left = dayCount(nyToday(now), h.actBy);
  const late = left < 0;
  const day = long ? `act by ${listDate(h.actBy)}` : listDate(h.actBy);
  const text = late && h.state === 'waiting' ? `Overdue, ${day}` : day;
  return {
    text: text.charAt(0).toUpperCase() + text.slice(1),
    cls: late ? 'actby late' : left <= 3 ? 'actby soon' : 'actby',
    tip: actByRule(h),
  };
}

/** "Follow up today" or "Follow up Nov 14" next to the Act by date on With me. */
export function followUpText(day: string | null | undefined, now: Date): string | null {
  if (!day) return null;
  return day === nyToday(now) ? 'Follow up today' : `Follow up ${listDate(day)}`;
}

const typedDay = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/** The Follow up on input's text for a day already set: "Nov 14, 2026". */
export const followUpInput = (day: string | null | undefined) =>
  day ? typedDay.format(new Date(`${day}T00:00:00Z`)) : '';

/**
 * What the AE typed in Follow up on (a typed date, never the browser's picker): the day, or why it
 * cannot be one. Today or later, as the server takes it. Null while the box is empty.
 */
export function parseFollowUp(raw: string, now: Date): { day: string } | { error: string } | null {
  if (!raw.trim()) return null;
  const day = parseDate(raw);
  if (!day) return { error: 'Enter a date like Nov 14, 2026.' };
  if (day < nyToday(now)) return { error: 'Pick today or a later day.' };
  return { day };
}

/** Under the Follow up on box (the Record and Renewals boards). */
export const followUpHelp = {
  record: 'Shows on the record and in Renewals, and comes back to the top on that day.',
  quick: 'Shows on the row and comes back to the top on that day.',
};

/** The toast after Set or Clear; its Undo puts the day before back. */
export const followUpSetText = (day: string | null) =>
  day ? `Follow up set for ${listDate(day)}.` : 'Follow up cleared.';

export const followUpUndoneText = 'Undone. The follow up is as it was.';
export const followUpUndoFailedText =
  'Could not undo. The renewal changed since, or 10 minutes passed.';

/** Why Set or Clear did not go through, from the server's answer. */
export function followUpFailedText(status: number, error: string | undefined): string {
  if (error === 'renewal_closed') return 'This renewal is closed now. Read it again.';
  if (error === 'bad_request') return 'Pick today or a later day.';
  if (status === 409 || status === 403) {
    return 'This handoff changed since it was read. Read it again.';
  }
  return 'Could not save the follow up. Try again.';
}

/** From: who sent it to the AE (the VA, the Manager, or the AE who passed it on). */
export function fromName(
  r: Pick<RenewalRow, 'handoff' | 'finishedHandoff'>,
  finished = false,
): string {
  return shownHandoff(r, finished)?.createdBy.name ?? '';
}

/** Sent: "Today at 10:14", "Nov 10 at 09:02" (the Renewals board). */
export function sentText(h: Pick<OpenHandoff, 'createdAt'>, now: Date): string {
  const text = sentWhen(h.createdAt, now);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const outcomeLabels: Record<NonNullable<FinishedHandoff['outcome']>, string> = {
  renewed: 'Renewed',
  rewritten: 'Rewritten',
  lost: 'Lost',
  not_renewed: 'Not renewed',
};

/** The Finished tab's Result: how it ended, by whom, and the day (the Renewals board). */
export function resultText(f: FinishedHandoff, meId: string): string {
  const day = listDate(nyToday(new Date(f.finishedAt)));
  const who = (p: { id: string; name: string } | null) =>
    !p ? 'someone no longer in the portal' : p.id === meId ? 'you' : p.name;
  const back = f.from?.name ?? 'the Unassigned pool';
  switch (f.state) {
    case 'sent_back':
      return `Sent back to ${back}, ${day}`;
    case 'passed':
      return `Passed to ${f.passedTo?.name ?? 'another AE'}, ${day}`;
    case 'returned':
      return `Returned to ${back}, ${day}`;
    case 'closed':
      return `${f.outcome ? outcomeLabels[f.outcome] : 'Closed'}. Closed by ${who(f.finishedBy)}, ${day}`;
    case 'cancelled': {
      // Ended by a reassign or a take over (spec C6): "Cancelled by Rachel Gold, reassigned to Eli
      // Marsh, Nov 12".
      const e = f.endedBy;
      const why = !e
        ? ''
        : e.why === 'taken_over'
          ? `, taken over by ${who(e.to)}`
          : `, reassigned to ${e.to ? who(e.to) : 'the Unassigned pool'}`;
      return `Cancelled by ${who(f.finishedBy)}${why}, ${day}`;
    }
  }
}

/** What an answer needs of the renewal: its handoff, still waiting, and the dialog's subline. */
export type AnswerRow = Pick<RenewalRow, 'id' | 'insured' | 'carrier' | 'line' | 'policyNumber'> & {
  handoff: OpenHandoff;
  /** The renewal premium, for Close it myself's sheet comment ("Renewed $1,996, DW"). */
  renewalPremium?: number | null;
};

/** A row whose handoff waits on this person, as Accept and Send back need it; null otherwise. */
export const answerRow = (
  r: Pick<RenewalRow, 'id' | 'insured' | 'carrier' | 'line' | 'policyNumber' | 'handoff'>,
  meId: string,
): AnswerRow | null =>
  r.handoff?.state === 'waiting' && r.handoff.to.id === meId ? { ...r, handoff: r.handoff } : null;

/** A row whose handoff is with this person, accepted, as Finish handoff needs it; null otherwise. */
export const withMeRow = (
  r: Pick<RenewalRow, 'id' | 'insured' | 'carrier' | 'line' | 'policyNumber' | 'handoff'> & {
    renewalPremium?: number | null;
  },
  meId: string,
): AnswerRow | null =>
  r.handoff?.state === 'accepted' && r.handoff.to.id === meId ? { ...r, handoff: r.handoff } : null;

/** A row whose handoff is on this person, waiting or with them, as Pass needs it; null otherwise. */
export const passRow = (
  r: Pick<RenewalRow, 'id' | 'insured' | 'carrier' | 'line' | 'policyNumber' | 'handoff'>,
  meId: string,
): AnswerRow | null => answerRow(r, meId) ?? withMeRow(r, meId);

/** Who a send back returns it to: the person the work came from, or the Unassigned pool. */
export const sendBackTo = (h: Pick<OpenHandoff, 'from'>) => h.from?.name ?? 'the Unassigned pool';

/** Accept's toast (C7: "Halpern, M. accepted. Status: Increase."). */
export const acceptedText = (insured: string, status: RenewalStatus) =>
  `${insured} accepted. Status: ${statuses[status].label}.`;

/** The bulk bar's Accept: one confirm for them all (spec C6: "Accept 3"). */
export const bulkAcceptConfirm = (n: number) => ({
  title: n === 1 ? 'Accept 1 handoff?' : `Accept ${n} handoffs?`,
  line: n === 1 ? 'It moves to your queue.' : 'They move to your queue.',
  action: `Accept ${n}`,
});

/** The bulk Accept's toast: how many, and how many were skipped because they changed. */
export function bulkAcceptedText(accepted: number, skipped: number): string {
  const done = accepted === 1 ? '1 handoff accepted.' : `${accepted} handoffs accepted.`;
  if (skipped === 0) return done;
  return skipped === 1
    ? `${done} 1 was skipped: it changed since the list was read.`
    : `${done} ${skipped} were skipped: they changed since the list was read.`;
}

/** Send back's confirm popup (the Renewals board). */
export const sendBackConfirm = (insured: string, h: Pick<OpenHandoff, 'from'>) => ({
  title: `Send ${insured} back to ${sendBackTo(h)}?`,
  line: h.from
    ? 'It returns to their queue at the same step with your note.'
    : 'It returns to the pool at the same step with your note.',
});

/** Under the note box: who reads the note. */
export const sendBackHelp = (h: Pick<OpenHandoff, 'from'>) =>
  h.from
    ? `${h.from.name} sees your note at the top of the record.`
    : 'Your note shows at the top of the record.';

export const sentBackText = (insured: string, h: Pick<OpenHandoff, 'from'>) =>
  `${insured} sent back to ${sendBackTo(h)}.`;

/** Why Accept or Send back did not go through, from the server's answer. */
export function answerFailedText(status: number, error: string | undefined): string {
  if (error === 'renewal_closed') return 'This renewal is closed now. Read the list again.';
  if (status === 409 || status === 403) {
    return 'This handoff changed since the list was read. Read the list again.';
  }
  return 'Could not save your answer. Try again.';
}

export const answerUndoneText = 'Undone. It waits for your answer again.';
export const answerUndoFailedText =
  'Could not undo. The renewal changed since, or 10 minutes passed.';

/** The AE's banner on the Record (the Record board): its title and the From line. */
export function aeBannerTitle(h: OpenHandoff): { title: string; from: string } {
  const sent = sentWhen(h.createdAt, new Date());
  const accepted =
    h.state === 'accepted' && h.answeredAt
      ? `. Accepted ${when(h.answeredAt, new Date()).replace(/ \d\d:\d\d$/, '')}`
      : '';
  if (h.kind === 'reassign_to_ae') {
    return { title: 'Reassign request for you', from: `Requested by ${h.createdBy.name}, ${sent}` };
  }
  if (h.kind === 'take_over') {
    return { title: 'You took over this renewal', from: `${sent}${accepted}` };
  }
  return {
    title: h.state === 'accepted' ? 'Handoff with you' : 'Handoff waiting for your answer',
    from: `From ${h.createdBy.name}, ${sent}${accepted}`,
  };
}

/**
 * The line the VA reads at the top of the Record after a send back or a return (Record board):
 * "Returned by Dana Wolfe at Email insured, Nov 12 14:20: ...". `step` is Continue at's name.
 */
export function backFromAeText(b: BackFromAe, now: Date, step: string | null = null): string {
  const by = b.by?.name ?? 'someone no longer in the portal';
  // A rewrite returned for the bank step (S15 batch 6c): the new policy, which goes to the bank.
  if (b.state === 'returned' && b.rewrite) {
    const w = b.rewrite;
    return `Rewritten to ${w.carrier} by ${by}, new policy # ${w.policyNumber}, ${money(w.premium)}. Back at ${step ?? 'Proof to bank'} for the bank step, ${when(b.at, now)}: ${b.note}`;
  }
  const what = b.state === 'sent_back' ? 'Sent back' : 'Returned';
  const at = b.state === 'returned' && step ? ` at ${step}` : '';
  return `${what} by ${by}${at}, ${when(b.at, now)}: ${b.note}`;
}

/** Finish handoff's first choice (the Record board): "Return to Tess Rowan". */
export const returnChoice = (h: Pick<OpenHandoff, 'from'>) =>
  h.from ? `Return to ${h.from.name}` : 'Return to the Unassigned pool';

/** Under the Outcome note: where it is kept and who reads it. */
export const returnNoteHelp = (h: Pick<OpenHandoff, 'from'>) =>
  `Saved to the portal log. ${h.from ? `${h.from.name} sees it` : 'It shows'} at the top of the record.`;

/** Return's confirm popup (the Record board); `step` is Continue at's name. */
export const returnConfirm = (insured: string, h: Pick<OpenHandoff, 'from'>, step: string) => ({
  title: `Return ${insured} to ${sendBackTo(h)}?`,
  line: h.from
    ? `It goes back to their queue at ${step} with your outcome note.`
    : `It goes back to the pool at ${step} with your outcome note.`,
});

/** Return's toast, with Undo (the Record board). */
export const returnedText = (insured: string, h: Pick<OpenHandoff, 'from'>, step: string) =>
  `${insured} returned to ${sendBackTo(h)} at ${step}.`;

export const returnUndoneText = 'Undone. The renewal is with you again.';

/** Why Return did not go through, from the server's answer. */
export function returnFailedText(status: number, error: string | undefined): string {
  if (error === 'step_done') return 'That step is done now. Pick where it stopped or a later step.';
  if (error === 'no_dec_page') return 'There is no dec page yet, so it goes back at step 1.';
  if (error === 'step_waiting') {
    return 'Proof to bank waits on the insured. Pick Proof to bank or an earlier step.';
  }
  if (error === 'renewal_closed') return 'This renewal is closed now. Read it again.';
  if (status === 409 || status === 403) {
    return 'This handoff or its steps changed since they were read. Read it again.';
  }
  return 'Could not return it. Try again.';
}

/**
 * Copy for AMS360 on a return (the Record board): the activity description from the Log activity
 * step's ("26-27 HO NYCM renewal" gives "26-27 HO NYCM rwl reviewed by AE, returned to VA") and the
 * sheet comment ("Returned to VA at Email insured, DW").
 */
export function returnAmsCopy(description: string, step: string, initials: string) {
  const term = description.replace(/\s+renewal\b.*$/, '').trim();
  return {
    activity: `${term} rwl reviewed by AE, returned to VA`,
    sheet: `Returned to VA at ${step}, ${initials}`,
  };
}

/** Close it myself's "How did it end?" (spec C6, the Record board), with the status each sets. */
export const endOptions: { key: HandoffClose['outcome']; label: string; status: RenewalStatus }[] =
  [
    { key: 'renewed', label: 'Renewed', status: 'done' },
    { key: 'rewritten', label: 'Rewritten with another carrier', status: 'done' },
    { key: 'lost', label: 'Lost', status: 'cancelled' },
    { key: 'not_renewed', label: 'Not renewed', status: 'cancelled' },
  ];

/** Lost's reasons (spec C6). */
export const lostReasonOptions: { key: LostReason; label: string }[] = [
  { key: 'price', label: 'Price' },
  { key: 'coverage', label: 'Coverage' },
  { key: 'service', label: 'Service' },
  { key: 'no_response', label: 'No response' },
  { key: 'other', label: 'Other' },
];

/** The status a close gives: Done or Cancelled, in words. */
export const closeStatusLabel = (outcome: HandoffClose['outcome']) =>
  outcome === 'renewed' || outcome === 'rewritten' ? 'Done' : 'Cancelled';

/**
 * The fields of Close it myself as typed, so the dialog and Copy for AMS360 read the same things.
 * `premium` is the typed text; `bank` only counts for a rewrite.
 */
export type CloseForm = {
  end: HandoffClose['outcome'] | null;
  carrier: string;
  premium: string;
  policyNumber: string;
  bank: boolean;
  lostReason: LostReason | null;
  wentTo: string;
  reason: string;
};

export const emptyCloseForm: CloseForm = {
  end: null,
  carrier: '',
  premium: '',
  policyNumber: '',
  bank: false,
  lostReason: null,
  wentTo: '',
  reason: '',
};

/**
 * What the server takes from the form, or why it cannot be sent yet (the first field missing). A
 * rewrite's premium is an amount like 1,996; its policy number as the steps take one.
 */
export function closeOf(f: CloseForm): { how: HandoffClose } | { missing: string } {
  switch (f.end) {
    case null:
      return { missing: 'Pick how it ended.' };
    case 'renewed':
      return { how: { outcome: 'renewed' } };
    case 'rewritten': {
      if (!f.carrier.trim()) return { missing: 'Pick the new carrier.' };
      const p = parseInput('totalPremium', f.premium);
      if (!p || 'error' in p) return { missing: p?.error ?? 'Enter the new premium.' };
      if (Number(p.value) <= 0) return { missing: 'Enter the new premium.' };
      const pol = parseInput('policyNumber', f.policyNumber);
      if (!pol || 'error' in pol) return { missing: pol?.error ?? 'Enter the new policy #.' };
      return {
        how: {
          outcome: 'rewritten',
          carrier: f.carrier.trim(),
          premium: Number(p.value),
          policyNumber: pol.value,
          bank: f.bank,
        },
      };
    }
    case 'lost':
      if (!f.lostReason) return { missing: 'Pick the reason.' };
      return {
        how: { outcome: 'lost', lostReason: f.lostReason, wentTo: f.wentTo.trim() || null },
      };
    case 'not_renewed':
      if (!f.reason.trim()) return { missing: 'Enter the reason.' };
      return { how: { outcome: 'not_renewed', reason: f.reason.trim() } };
  }
}

/**
 * Copy for AMS360 on Close it myself (the Record board): the activity description from the Log
 * activity step's ("26-27 HO NYCM renewal" gives "26-27 HO NYCM rwl renewed, advised insured") and
 * the sheet comment ("Renewed $1,996, DW"). `premium` is the renewal premium, for Renewed.
 */
export function closeAmsCopy(
  description: string,
  f: CloseForm,
  premium: number | null,
  initials: string,
) {
  const term = description.replace(/\s+renewal\b.*$/, '').trim();
  const lower = (s: string) => s.toLowerCase();
  switch (f.end) {
    case 'rewritten': {
      const c = f.carrier.trim() || '[carrier]';
      const p = parseInput('totalPremium', f.premium);
      const amount = p && 'value' in p ? ` ${money(Number(p.value))}` : '';
      return {
        activity: `${term} rwl rewritten to ${c}, advised insured`,
        sheet: `Rewritten to ${c}${amount}, ${initials}`,
      };
    }
    case 'lost': {
      const why = lostReasonOptions.find((o) => o.key === f.lostReason)?.label;
      const to = f.wentTo.trim();
      return {
        activity: `${term} rwl lost${to ? ` to ${to}` : ''}${why ? `, reason ${lower(why)}` : ''}`,
        sheet: `Lost${why ? `, ${lower(why)}` : ''}, ${initials}`,
      };
    }
    case 'not_renewed': {
      const r = f.reason.trim();
      return {
        activity: `${term} non-renewed${r ? `, ${r}` : ''}`,
        sheet: `Not renewed${r ? `, ${r}` : ''}, ${initials}`,
      };
    }
    default:
      return {
        activity: `${term} rwl renewed, advised insured`,
        sheet: `Renewed${premium !== null ? ` ${money(premium)}` : ''}, ${initials}`,
      };
  }
}

/** A rewrite that the VA still sends to the bank: back to them at Proof to bank (spec C6). */
export const toBank = (f: CloseForm) => f.end === 'rewritten' && f.bank;

/**
 * "VA still needs to do the bank step" is offered while Proof to bank is not done, on any line but
 * auto (never a bank step). The old dec page's reading does not decide it: the new policy's
 * mortgagee is on the new dec page, which is read only after it is uploaded (Codex on PR 305).
 */
export const bankStillToDo = (steps: Pick<StepsView, 'steps' | 'decPage'>) =>
  steps.steps.bank.state !== 'done' && steps.decPage.idCards === null;

/** Under the Outcome note on a close: where it is kept and the status it gets. */
export const closeNoteHelp = (outcome: HandoffClose['outcome'] | null) =>
  `Saved to the portal log. The status changes to ${outcome ? closeStatusLabel(outcome) : 'Done'}.`;

/** Close it myself's button (the Record board). */
export const closeButton = (f: CloseForm) =>
  toBank(f) ? 'Return to VA for bank step' : `Close as ${f.end ? closeStatusLabel(f.end) : 'Done'}`;

/** The confirm before a rewrite goes back to the VA for the bank step (work moves, spec C7). */
export const bankConfirm = (insured: string, h: Pick<OpenHandoff, 'from'>) => ({
  title: `Return ${insured} to ${sendBackTo(h)} for the bank step?`,
  line: h.from
    ? 'It goes back to their queue at Proof to bank with your outcome note.'
    : 'It goes back to the pool at Proof to bank with your outcome note.',
});

/** The toast after Close it myself (the Record board), with Undo. */
export const closedText = (insured: string, outcome: HandoffClose['outcome']) =>
  `${insured} closed as ${closeStatusLabel(outcome)}.`;

/** The toast after a rewrite went back for the bank step (the Record board), with Undo. */
export const rewrittenText = (insured: string, h: Pick<OpenHandoff, 'from'>) =>
  `${insured} rewritten. Back to ${sendBackTo(h)} for the bank step.`;

/** Why Close it myself did not go through, from the server's answer. */
export function closeFailedText(status: number, error: string | undefined): string {
  if (error === 'new_dec_page_required') return 'Upload the new dec page first.';
  if (error === 'bank_not_needed') return 'This renewal has no bank step. Close it instead.';
  if (error === 'bad_request')
    return 'Fill in how it ended, the outcome note and the sheet comment.';
  if (error === 'step_done') return 'Proof to bank is done now. Close it without the bank step.';
  if (error === 'step_waiting' || error === 'no_dec_page') {
    return 'The steps changed since they were read. Read it again.';
  }
  if (error === 'renewal_closed') return 'This renewal is closed now. Read it again.';
  if (status === 409 || status === 403) {
    return 'This handoff or its steps changed since they were read. Read it again.';
  }
  return 'Could not close it. Try again.';
}

/** Pass to another AE's confirm (the Renewals board; spec C6). */
export function passConfirm(rows: Pick<AnswerRow, 'insured'>[], ae: { name: string }) {
  const one = rows.length === 1;
  return {
    title: `Pass ${one ? rows[0].insured : `${rows.length} renewals`} to ${ae.name}?`,
    line: one
      ? `It keeps its status and shows Awaiting AE until ${ae.name} accepts.`
      : `They keep their status and show Awaiting AE until ${ae.name} accepts.`,
  };
}

/** Pass's toast (the Renewals board), with how many were skipped because they changed. */
export function passedText(rows: Pick<AnswerRow, 'insured'>[], ae: string, done: number): string {
  const head =
    done === 0
      ? 'Nothing was passed.'
      : rows.length === 1
        ? `${rows[0].insured} passed to ${ae}. Awaiting AE until they accept.`
        : `Passed ${done === 1 ? '1 renewal' : `${done} renewals`} to ${ae}. Awaiting AE until they accept.`;
  const skipped = rows.length - done;
  if (skipped === 0 || rows.length === 1) return head;
  return skipped === 1
    ? `${head} 1 was skipped: it changed since the list was read.`
    : `${head} ${skipped} were skipped: they changed since the list was read.`;
}

export const passUndoneText = 'Undone. The handoff is with you again.';

/** Why a pass did not go through, from the server's answer. */
export function passFailedText(status: number, error: string | undefined): string {
  if (error === 'bad_person') return 'That account executive cannot take work now. Pick another.';
  if (error === 'same') return 'It is already with that account executive.';
  return answerFailedText(status, error);
}

/** The rows a person may take over (spec C6; the Renewals board's canTake): the AE, not their own. */
export function canTakeOver(
  r: Pick<RenewalRow, 'handledBy' | 'handoff' | 'status'>,
  role: string,
  meId: string,
): boolean {
  if (role !== 'Account Executive') return false;
  if (r.status === 'done' || r.status === 'cancelled') return false;
  if (r.handledBy?.id === meId) return false;
  // Waiting on them: they answer it (Accept) instead.
  return !(r.handoff && r.handoff.to.id === meId);
}

/**
 * Take over's confirm (decision 1, Nov 1): no reason, the confirm names the holder and a handoff
 * waiting on another AE that it cancels. Several at once from Reassign: one confirm.
 */
export function takeOverConfirm(
  rows: Pick<RenewalRow, 'insured' | 'handledBy' | 'handoff'>[],
  meId: string,
): { title: string; line: string } {
  if (rows.length !== 1) {
    return { title: `Take over ${rows.length} renewals?`, line: 'They move to your queue.' };
  }
  const r = rows[0];
  const from = r.handledBy && r.handledBy.id !== meId ? r.handledBy.name : null;
  const waits =
    r.handoff?.state === 'waiting' && r.handoff.to.id !== meId
      ? ` ${r.handoff.to.name}'s pending handoff is cancelled.`
      : '';
  return from
    ? {
        title: `Take over ${r.insured} from ${from}?`,
        line: `It moves to your queue and ${from} is notified.${waits}`,
      }
    : { title: `Take over ${r.insured}?`, line: `It moves to your queue.${waits}` };
}

/** Take over's toast (the Renewals board), with Undo. */
export function tookOverText(rows: Pick<RenewalRow, 'insured'>[], done: number): string {
  if (done === 0) return 'Nothing was taken over.';
  const head =
    rows.length === 1
      ? `You took over ${rows[0].insured.replace(/\.$/, '')}.`
      : `You took over ${done === 1 ? '1 renewal' : `${done} renewals`}.`;
  const skipped = rows.length - done;
  if (skipped === 0 || rows.length === 1) return head;
  return `${head} ${skipped === 1 ? '1 was' : `${skipped} were`} left as they were: changed by someone else, closed, or held by the Manager or the Admin.`;
}

export const takeOverUndoneText = 'Undone. The renewal is back where it was.';

/** Why a take over did not go through, from the server's answer. */
export function takeOverFailedText(status: number, error: string | undefined): string {
  if (error === 'not_yours') return 'The Manager or the Admin holds it. Ask them to hand it over.';
  if (error === 'same') return 'It is already yours.';
  if (error === 'renewal_closed') return 'This renewal is closed now. Read the list again.';
  if (status === 409) return 'This renewal changed since the list was read. Read the list again.';
  return 'Could not take it over. Try again.';
}
